import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

interface EurdEntry {
  id: string;
  activeSubstance: string;
  procedure: string | null;
  dataLockPoint: string;
  frequencyMonths: number;
  nextDueDate: string;
  psurRequired: boolean;
  notes: string | null;
  lastSyncedAt: string;
  updatedAt: string;
}

const EURD_PUBLIC_URL =
  'https://www.ema.europa.eu/en/documents/other/list-european-union-reference-dates-eurd-list_en.xlsx';

function frequencyLabel(months: number): string {
  if (months === 6) return '6-monthly';
  if (months === 12) return 'Yearly';
  if (months === 36) return '3-yearly';
  if (months === 60) return '5-yearly';
  return `${months}-monthly`;
}

function statusFor(nextDueDate: string): {
  label: string;
  variant: 'success' | 'warning' | 'destructive' | 'secondary';
} {
  const now = Date.now();
  const due = new Date(nextDueDate).getTime();
  const days = Math.round((due - now) / 86_400_000);
  if (days < 0) return { label: `Overdue (${Math.abs(days)}d)`, variant: 'destructive' };
  if (days <= 30) return { label: `Due in ${days}d`, variant: 'warning' };
  if (days <= 90) return { label: `Due in ${days}d`, variant: 'success' };
  return { label: `Due in ${days}d`, variant: 'secondary' };
}

export function EurdListPage() {
  const [search, setSearch] = useState('');
  const [frequency, setFrequency] = useState('');

  const eurd = useQuery({
    queryKey: ['eurd', { search, frequency }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('pageSize', '500');
      if (search) params.set('activeSubstance', search);
      try {
        return (await api.get(`/api/eurd?${params.toString()}`)).data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const allItems: EurdEntry[] = eurd.data?.items ?? [];
  const items = frequency
    ? allItems.filter((e) => String(e.frequencyMonths) === frequency)
    : allItems;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">EURD list</h1>
          <p className="text-sm text-muted-foreground">
            EU Reference Dates list — active substances, Data Lock Points (DLP), submission
            frequency and next-due dates synced from EMA.
          </p>
        </div>
        <Button asChild variant="outline">
          <a href={EURD_PUBLIC_URL} target="_blank" rel="noreferrer">
            <ExternalLink className="mr-1.5 h-4 w-4" /> EMA source
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Active substance</Label>
              <Input
                placeholder="e.g. tozinameran, lecanemab"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div>
              <Label>Frequency</Label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="6">6-monthly</option>
                <option value="12">Yearly</option>
                <option value="36">3-yearly</option>
                <option value="60">5-yearly</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Entries ({items.length})</CardTitle>
          <CardDescription>
            Click an entry to see DLP, frequency, source URL and the next submission window.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Active substance</TableHead>
                <TableHead>DLP</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead>Next due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eurd.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!eurd.isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="p-10 text-center text-sm text-muted-foreground">
                    <Calendar className="mx-auto mb-2 h-6 w-6" /> No EURD entries match.
                  </TableCell>
                </TableRow>
              )}
              {items.map((e) => {
                const s = statusFor(e.nextDueDate);
                return (
                  <TableRow key={e.id}>
                    <TableCell>
                      <div className="text-sm font-medium">{e.activeSubstance}</div>
                      {e.procedure && (
                        <div className="text-xs text-muted-foreground">{e.procedure}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(e.dataLockPoint)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {frequencyLabel(e.frequencyMonths)}
                    </TableCell>
                    <TableCell className="text-xs">{formatDate(e.nextDueDate)}</TableCell>
                    <TableCell>
                      <Badge variant={s.variant}>{s.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="ghost">
                        <Link to={`/eurd/${e.id}`}>
                          Open <ArrowRight className="ml-1 h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
